import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Crop, Loader2, Pencil, RotateCw, Undo2, X } from 'lucide-react';
import { Button } from 'src/@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from 'src/@/components/ui/dialog';
import { Switch } from 'src/@/components/ui/switch';
import { Label } from 'src/@/components/ui/label';
import MediaPreviewStage from './MediaPreviewStage';
import MediaCaptionBar from './MediaCaptionBar';
import MediaFilmstrip from './MediaFilmstrip';
import { getMediaKind, isDocumentKind } from './utils/mediaTypes';
import { bakeTransform, composeFinalFile, hasImageEdits } from './utils/imageExport';

// ----------------------------------------------------------------------
// Overlay de pré-visualização de mídia estilo WhatsApp Web, confinado à área
// do ChatWindow via portal para o container (mediaOverlayRef).

const DRAW_COLORS = ['#ffffff', '#111827', '#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#7C3AED'];

let uidCounter = 0;
const nextUid = () => {
    uidCounter += 1;
    return `media_${Date.now()}_${uidCounter}`;
};

function createItem(file) {
    const kind = getMediaKind(file);
    return {
        id: nextUid(),
        file,
        originalUrl: file.preview || URL.createObjectURL(file),
        editedUrl: null,
        strokes: [],
        caption: '',
        kind,
        asDocument: isDocumentKind(kind),
    };
}

export default function MediaPreviewOverlay({ containerRef, initialFiles, chat, sending, onClose, onAddFiles, onRemoveFile, onSend }) {
    const { t } = useTranslation('chat');
    const [items, setItems] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [mode, setMode] = useState('view');
    const [drawColor, setDrawColor] = useState('#ef4444');
    const [exporting, setExporting] = useState(false);
    const [confirmClose, setConfirmClose] = useState(false);
    const editedUrlsRef = useRef(new Set());

    // Sincroniza arquivos vindos do ChatBottomBar preservando legendas/edições
    useEffect(() => {
        setItems((prev) => {
            const prevByFile = new Map(prev.map((item) => [item.file, item]));
            return (initialFiles || []).map((file) => prevByFile.get(file) || createItem(file));
        });
    }, [initialFiles]);

    useEffect(() => {
        if (items.length === 0) return;
        if (!items.some((item) => item.id === activeId)) {
            setActiveId(items[items.length - 1].id);
            setMode('view');
        }
    }, [items, activeId]);

    // Revoga apenas os object URLs criados pelas edições (os previews originais
    // seguem o ciclo de vida do estado `files` do ChatBottomBar)
    useEffect(() => {
        const urls = editedUrlsRef.current;
        return () => {
            urls.forEach((url) => URL.revokeObjectURL(url));
            urls.clear();
        };
    }, []);

    const activeItem = useMemo(() => items.find((item) => item.id === activeId), [items, activeId]);
    const hasChanges = items.some((item) => item.caption?.trim() || hasImageEdits(item));
    const isMetaChannel = chat?.canal === 'instagram' || chat?.canal === 'messenger';

    const updateItem = (id, patch) => {
        setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
    };

    const handleClose = () => {
        if (sending || exporting) return;
        if (hasChanges) {
            setConfirmClose(true);
            return;
        }
        onClose();
    };

    const handleCaptionChange = (value) => {
        if (!activeItem) return;
        const next = typeof value === 'function' ? value(activeItem.caption) : value;
        updateItem(activeItem.id, { caption: next });
    };

    const registerEditedUrl = (url) => {
        editedUrlsRef.current.add(url);
        return url;
    };

    const handleRotate = async () => {
        if (!activeItem || exporting) return;
        try {
            const baked = registerEditedUrl(
                await bakeTransform(activeItem.editedUrl || activeItem.originalUrl, { rotation: 90 })
            );
            // Rotaciona também as coordenadas dos traços (90° horário: x,y -> 1-y,x)
            const strokes = activeItem.strokes.map((stroke) => ({
                ...stroke,
                points: stroke.points.map((point) => ({ x: 1 - point.y, y: point.x })),
            }));
            updateItem(activeItem.id, { editedUrl: baked, strokes });
        } catch (error) {
            console.error('handleRotate error', error);
        }
    };

    const handleApplyCrop = async ({ rotation, croppedAreaPixels }) => {
        if (!activeItem) return;
        if (!croppedAreaPixels && !rotation) {
            setMode('view');
            return;
        }
        if (activeItem.strokes.length > 0 && !window.confirm(t('mediaEditor.discardDrawings'))) {
            return;
        }
        try {
            const baked = registerEditedUrl(
                await bakeTransform(activeItem.editedUrl || activeItem.originalUrl, {
                    rotation,
                    croppedAreaPixels,
                })
            );
            updateItem(activeItem.id, { editedUrl: baked, strokes: [] });
            setMode('view');
        } catch (error) {
            console.error('handleApplyCrop error', error);
        }
    };

    const handleRemove = (id) => {
        const target = items.find((item) => item.id === id);
        if (!target) return;
        const remaining = items.filter((item) => item.id !== id);
        if (remaining.length === 0) {
            onClose();
            return;
        }
        setItems(remaining);
        // Remove também do estado `files` do pai, senão o arquivo ressuscita
        // na próxima sincronização (ex.: ao adicionar outro pelo "+")
        onRemoveFile?.(target.file);
    };

    const handleSend = async () => {
        if (sending || exporting || items.length === 0) return;
        if (mode !== 'view') setMode('view');
        setExporting(true);
        try {
            const processed = [];
            for (const item of items) {
                let fileToSend = item.file;
                if (item.kind === 'image' && hasImageEdits(item)) {
                    fileToSend = await composeFinalFile(
                        item.editedUrl || item.originalUrl,
                        item.strokes,
                        item.file
                    );
                }
                processed.push({
                    file: fileToSend,
                    caption: item.caption?.trim() || '',
                    isDocument: item.asDocument,
                });
            }
            await onSend(processed);
        } catch (error) {
            console.error('handleSend media editor error', error);
        } finally {
            setExporting(false);
        }
    };

    // Esc fecha o overlay
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                if (confirmClose) {
                    setConfirmClose(false);
                } else if (mode !== 'view') {
                    setMode('view');
                } else {
                    handleClose();
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    });

    if (!items.length) return null;

    const isImage = activeItem?.kind === 'image';
    const busy = sending || exporting;

    const overlay = (
        <div
            className={`${containerRef?.current ? 'absolute' : 'fixed'} inset-0 z-40 flex flex-col bg-white dark:bg-slate-900 animate-in fade-in duration-200`}
        >
            {/* Header */}
            <div className="flex items-center justify-between h-14 px-4 border-b border-gray-100 dark:border-slate-800 flex-shrink-0">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleClose}>
                        <X className="h-5 w-5 text-slate-700 dark:text-slate-200" />
                    </Button>
                    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {t('mediaEditor.title')}
                    </span>
                </div>

                {isImage && (
                    <div className="flex items-center gap-1">
                        {mode === 'draw' && (
                            <>
                                <div className="flex items-center gap-1.5 mr-2">
                                    {DRAW_COLORS.map((color) => (
                                        <button
                                            key={color}
                                            type="button"
                                            onClick={() => setDrawColor(color)}
                                            className={`w-6 h-6 rounded-full border border-gray-200 dark:border-slate-600 transition-transform ${
                                                drawColor === color ? 'ring-2 ring-offset-1 ring-[#7C3AED] scale-110' : ''
                                            }`}
                                            style={{ backgroundColor: color }}
                                        />
                                    ))}
                                </div>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    title={t('mediaEditor.undo')}
                                    disabled={!activeItem?.strokes?.length}
                                    onClick={() =>
                                        updateItem(activeItem.id, { strokes: activeItem.strokes.slice(0, -1) })
                                    }
                                >
                                    <Undo2 className="h-4 w-4" />
                                </Button>
                                <div className="w-px h-6 bg-gray-200 dark:bg-slate-700 mx-1" />
                            </>
                        )}

                        <Button
                            variant="ghost"
                            size="icon"
                            className={`h-8 w-8 ${mode === 'crop' ? 'bg-violet-50 text-[#7C3AED] dark:bg-violet-500/20' : ''}`}
                            title={t('mediaEditor.crop')}
                            disabled={busy}
                            onClick={() => setMode(mode === 'crop' ? 'view' : 'crop')}
                        >
                            <Crop className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            title={t('mediaEditor.rotate')}
                            disabled={busy || mode === 'crop'}
                            onClick={handleRotate}
                        >
                            <RotateCw className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            className={`h-8 w-8 ${mode === 'draw' ? 'bg-violet-50 text-[#7C3AED] dark:bg-violet-500/20' : ''}`}
                            title={t('mediaEditor.draw')}
                            disabled={busy || mode === 'crop'}
                            onClick={() => setMode(mode === 'draw' ? 'view' : 'draw')}
                        >
                            <Pencil className="h-4 w-4" />
                        </Button>
                    </div>
                )}
            </div>

            {/* Stage */}
            <div className="flex-1 min-h-0 flex items-center justify-center p-4 bg-slate-50 dark:bg-slate-950/40 relative">
                <MediaPreviewStage
                    item={activeItem}
                    mode={mode}
                    drawColor={drawColor}
                    onStrokesChange={(strokes) => updateItem(activeItem.id, { strokes })}
                    onApplyCrop={handleApplyCrop}
                    onCancelCrop={() => setMode('view')}
                />
                {exporting && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-slate-900/60">
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {t('mediaEditor.exporting')}
                        </div>
                    </div>
                )}
            </div>

            {/* Legenda + filmstrip */}
            <div className="flex-shrink-0 border-t border-gray-100 dark:border-slate-800 px-4 pt-3">
                <MediaCaptionBar
                    value={activeItem?.caption || ''}
                    onChange={handleCaptionChange}
                    onSubmit={handleSend}
                    disabled={busy}
                />
                {!isMetaChannel && (
                    <div className="mx-auto w-full max-w-2xl flex items-center justify-end gap-2 mt-2">
                        <Switch
                            id="media-editor-send-as-document"
                            checked={Boolean(activeItem?.asDocument)}
                            disabled={busy || isDocumentKind(activeItem?.kind)}
                            onCheckedChange={(checked) => activeItem && updateItem(activeItem.id, { asDocument: checked })}
                        />
                        <Label
                            htmlFor="media-editor-send-as-document"
                            className="text-xs text-gray-500 cursor-pointer"
                        >
                            {t('mediaEditor.sendAsDocument')}
                        </Label>
                    </div>
                )}
            </div>
            <MediaFilmstrip
                items={items}
                activeId={activeId}
                onSelect={(id) => {
                    setActiveId(id);
                    setMode('view');
                }}
                onRemove={handleRemove}
                onAdd={onAddFiles}
                onSend={handleSend}
                sending={busy}
            />

            {/* Confirmação de descarte ao sair */}
            <Dialog open={confirmClose} onOpenChange={setConfirmClose}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>{t('mediaEditor.discardConfirm')}</DialogTitle>
                        <DialogDescription>{t('mediaEditor.discardMessage')}</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2 sm:gap-2">
                        <Button variant="ghost" onClick={() => setConfirmClose(false)}>
                            {t('mediaEditor.keepEditing')}
                        </Button>
                        <Button
                            className="bg-red-600 hover:bg-red-700 text-white"
                            onClick={() => {
                                setConfirmClose(false);
                                onClose();
                            }}
                        >
                            {t('mediaEditor.discard')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );

    return createPortal(overlay, containerRef?.current || document.body);
}
